import { HTMLProps, useContext, useEffect, useRef } from 'react';
import { FundRatesContext } from '../../../../contexts/funds-robot/FundRatesContext';
import useIsVisible from '../../../../hooks/useIsVisible';
import { Asset } from '../../../../types/Asset';
import { toPrice, toPercentage } from '../../../../utils/parsers';
import { ComparisonScreenContext } from '../ComparisonScreen';
import { useGetYearlyReturnsFund } from '../../../../api/funds-robot/useGetYearlyReturnsFund';
import { useGetYearlyReturnsModelPortfolio } from '../../../../api/funds-robot/useGetYearlyReturnsModelPortfolio';
import { useTranslation } from 'react-i18next';
import riskImages from '../../../../assets/funds-robot/afm-risk-images';
import { getRefURL } from '../../../../api/referenceLocations';
import { AFM_URL } from '../../../../config/constants';
import { DateTime, Interval } from 'luxon';
import { Link } from '../../../general/Link';

export const FundCompareTopSection: React.FC<
    {
        fund: Asset;
        tint: string;
        startDate: string;
        endDate: string;
        index: number;
    } & HTMLProps<HTMLDivElement>
> = ({ fund, tint, startDate, endDate, index, className }) => {
    const { t } = useTranslation('funds');
    const fundRatesContext = useContext(FundRatesContext);
    const comparisonScreenContext = useContext(ComparisonScreenContext);

    const ref = useRef<HTMLDivElement | null>(null);
    const isVisible = useIsVisible(ref);

    useEffect(() => {
        comparisonScreenContext.setIsHeaderVisible(isVisible);
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const rates = fundRatesContext.rates.get(fund.id);

    const selectedInterval =
        rates &&
        rates.withinInterval.first &&
        rates.withinInterval.last &&
        Interval.fromDateTimes(rates.withinInterval.first.date, rates?.withinInterval.last?.date);

    const yearStatsModelPortfolio = useGetYearlyReturnsModelPortfolio(
        DateTime.fromISO(fund.basicCharacteristics.startDate),
        fund.category === 'Portfolio' ? fund.id : undefined,
        selectedInterval,
    );
    const yearStatsFund = useGetYearlyReturnsFund(
        DateTime.fromISO(fund.basicCharacteristics.startDate),
        fund.category !== 'Portfolio' ? fund.id : undefined,
        fund.category !== 'Portfolio' ? fund.dividendPayments : undefined,
        selectedInterval,
    );

    return (
        <div className={`flex flex-col flex-1 min-w-0 ${className}`}>
            <div className={`relative pt-8 pb-6 px-8 text-lg flex flex-row items-start bg-white`} ref={ref}>
                <div className="mr-3 flex items-center justify-center" style={{ height: 'calc(1.5 * var(--srem))' }}>
                    <div className="rounded-full w-4 h-4" style={{ backgroundColor: tint }}></div>
                </div>
                <div
                    className="ellipsis overflow-hidden"
                    style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        minHeight: 'calc(3 * var(--srem))',
                    }}
                >
                    {fund.name}
                </div>
            </div>
            {rates?.withinInterval.first && rates.withinInterval.last && (
                <div className="mt-0 px-8">
                    <div className="flex flex-col ">
                        {fund.category !== 'Portfolio' && rates.allTime.raw.length > 0 && (
                            <span className="flex flex-row justify-between">
                                <span>{t('funds-comparison-screen-current-rate')}</span>
                                <span>{toPrice(rates.allTime.raw[0].y)}</span>
                            </span>
                        )}
                        {(fund.category === 'Stock' || fund.category === 'Bond') && (
                            <>
                                <span className="flex flex-row justify-between mt-2">
                                    <span>{t('funds-comparison-screen-highest-value')}</span>
                                    <span>{toPrice(rates.withinInterval.highestRaw)}</span>
                                </span>
                                <span className="flex flex-row justify-between mt-2">
                                    <span>{t('funds-comparison-screen-lowest-value')}</span>
                                    <span>{toPrice(rates.withinInterval.lowestRaw)}</span>
                                </span>
                            </>
                        )}
                    </div>
                    <div className="flex flex-row justify-between items-center mt-4">
                        <div className="flex flex-col">
                            <span>{t('funds-comparison-screen-returns-in-interval')}</span>
                            <span className="font-medium">
                                {rates?.withinInterval.first.date.toFormat('d/M/yyyy')} -{' '}
                                {rates?.withinInterval.last.date.toFormat('d/M/yyyy')}
                            </span>
                        </div>
                        {selectedInterval && selectedInterval.length('years') < 1 && (
                            <span>{toPercentage(rates.withinInterval.last.indexed - 100)}</span>
                        )}
                    </div>
                    {selectedInterval && selectedInterval.length('years') >= 1 && (
                        <div className="flex flex-row justify-between items-center mt-4">
                            <div className="flex flex-col">
                                <span>{t('funds-comparison-screen-total-returns')}</span>
                            </div>
                            <span>{toPercentage(rates.withinInterval.last.indexed - 100)}</span>
                        </div>
                    )}
                    {
                        // We only show annual return if the interval is longer than 1 year. Interval can be shorter than selection if data is missing.
                        (yearStatsFund || yearStatsModelPortfolio) &&
                            selectedInterval &&
                            selectedInterval.length('years') >= 1 && (
                                <div className="flex flex-row justify-between items-center mt-4">
                                    <div className="flex flex-col">
                                        <span>{t('funds-comparison-screen-annual-returns')}</span>
                                    </div>
                                    {yearStatsFund && yearStatsFund.annual && (
                                        <span>{toPercentage(yearStatsFund.annual)}</span>
                                    )}
                                    {yearStatsModelPortfolio && yearStatsModelPortfolio.annual && (
                                        <span>{toPercentage(yearStatsModelPortfolio.annual)}</span>
                                    )}
                                </div>
                            )
                    }
                    {fund.riskCharacteristics.classification && riskImages[fund.riskCharacteristics.classification] && (
                        <div className="flex flex-row justify-center items-center mt-4 mb-4 pb-8 border-gray-300 border-b-2">
                            <div className="flex flex-col">
                                <Link href={AFM_URL} target="_blank" rel="noreferrer">
                                    <img
                                        className="max-h-72 min-h-180"
                                        src={getRefURL() + riskImages[fund.riskCharacteristics.classification]}
                                        alt={'Risk classification ' + fund.riskCharacteristics.classification}
                                    ></img>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
